import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { Save, Bold, Italic, List, ListOrdered, Type, AlertCircle, CheckCircle } from 'lucide-react';

const Policies = ({ isSuperAdmin }) => {
  const [policies, setPolicies] = useState({
    privacy_policy: '',
    refund_policy: '',
    shipping_policy: '',
    terms_of_service: ''
  });

  const [activePolicy, setActivePolicy] = useState('privacy_policy');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const textareaRef = useRef(null);
  

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const policyTabs = [
    { key: 'privacy_policy', label: 'Privacy Policy', icon: 'Lock' },
    { key: 'refund_policy', label: 'Refund Policy', icon: 'Refresh' },
    { key: 'shipping_policy', label: 'Shipping Policy', icon: 'Truck' },
    { key: 'terms_of_service', label: 'Terms of Service', icon: 'File' }
  ];

  useEffect(() => {
    if (!isSuperAdmin) loadPolicies();
  }, [isSuperAdmin]);

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/policies`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setPolicies(data.policies);
      } else {
        setError('Failed to load policies');
      }
    } catch (error) {
      setError('Network error occurred');
    }
    setLoading(false);
  };

  const handlePolicyChange = (value) => {
    setPolicies(prev => ({
      ...prev,
      [activePolicy]: value
    }));
  };

  const insertFormatting = (startTag, endTag) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    let newText;
    if (selectedText) {
      // Wrap selected text
      newText = beforeText + startTag + selectedText + endTag + afterText;
    } else {
      // Insert tags at cursor position
      newText = beforeText + startTag + endTag + afterText;
    }

    handlePolicyChange(newText);

    // Set cursor position
    setTimeout(() => {
      const newPosition = selectedText ? end + startTag.length + endTag.length : start + startTag.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 0);
  };

  const formatBold = () => insertFormatting('**', '**');
  const formatItalic = () => insertFormatting('*', '*');
  const formatHeading = () => insertFormatting('## ', '');
  const formatBulletList = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(start);
    
    const newText = beforeText + '\n• ' + afterText;
    handlePolicyChange(newText);
    
    setTimeout(() => {
      textarea.setSelectionRange(start + 3, start + 3);
      textarea.focus();
    }, 0);
  };

  const formatNumberedList = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(start);
    
    const newText = beforeText + '\n1. ' + afterText;
    handlePolicyChange(newText);
    
    setTimeout(() => {
      textarea.setSelectionRange(start + 4, start + 4);
      textarea.focus();
    }, 0);
  };

  const savePolicies = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/policies`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(policies)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Policies saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save policies');
      }
    } catch (error) {
      setError('Network error occurred');
    }
    setSaving(false);
  };

  const saveSinglePolicy = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/policies/single`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          policy_type: activePolicy,
          content: policies[activePolicy]
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`${policyTabs.find(tab => tab.key === activePolicy)?.label} saved successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save policy');
      }
    } catch (error) {
      setError('Network error occurred');
    }
    setSaving(false);
  };

  // Convert markdown-like formatting to display format
  const previewText = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^• (.*$)/gm, '<li>$1</li>')
      .replace(/^(\d+)\. (.*$)/gm, '<li>$1. $2</li>')
      .replace(/\n/g, '<br>');
  };

  if (isSuperAdmin) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Policies Management</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <p>Store-level settings. Log in as a provider to manage a store&apos;s policies.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Policies Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={saveSinglePolicy}
            disabled={saving}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Save Current</span>
          </button>
          <button
            onClick={savePolicies}
            disabled={saving}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>Save All</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        {/* Policy Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {policyTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActivePolicy(tab.key)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activePolicy === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Editor Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit {policyTabs.find(tab => tab.key === activePolicy)?.label}
              </h3>
            </div>

            {/* Formatting Toolbar */}
            <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-md border">
              <button
                onClick={formatHeading}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Heading"
              >
                <Type className="h-4 w-4" />
              </button>
              <button
                onClick={formatBold}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                onClick={formatItalic}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </button>
              <div className="border-l border-gray-300 h-6 mx-2"></div>
              <button
                onClick={formatBulletList}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Bullet List"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={formatNumberedList}
                className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                title="Numbered List"
              >
                <ListOrdered className="h-4 w-4" />
              </button>
            </div>

            {/* Text Editor */}
            <textarea
              ref={textareaRef}
              value={policies[activePolicy]}
              onChange={(e) => handlePolicyChange(e.target.value)}
              placeholder={`Enter your ${policyTabs.find(tab => tab.key === activePolicy)?.label.toLowerCase()}...`}
              className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
            />

            {/* Formatting Help */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Formatting Guide:</h4>
              <div className="text-xs text-blue-800 space-y-1">
                <div><code>## Heading</code> → Heading</div>
                <div><code>**Bold text**</code> → <strong>Bold text</strong></div>
                <div><code>*Italic text*</code> → <em>Italic text</em></div>
                <div><code>• List item</code> → Bullet point</div>
                <div><code>1. List item</code> → Numbered list</div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
            <div className="border border-gray-300 rounded-md p-4 h-96 overflow-y-auto bg-gray-50">
              {policies[activePolicy] ? (
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: previewText(policies[activePolicy]) 
                  }}
                />
              ) : (
                <p className="text-gray-500 italic">
                  Start typing to see preview...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policies;